import { useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { WORKSPACE } from "@/constants/testIds";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Search, MoreVertical, Trash2, Pencil, Lock, FileSpreadsheet, FileText, Presentation, File as FileIcon, CalendarClock, Archive, MessageSquareText, ArrowUpDown, Check, ChevronDown, ChevronRight, Clock, FilePlus2, ArchiveRestore } from "lucide-react";

const MAX_MB = 800;
const ACCEPT_EXT = [".xlsx", ".xlsm", ".pdf", ".doc", ".docx", ".ppt", ".pptx"];

export default function FileSidebar({ files, loading, activeFileId, onSelect, onRefresh, currentUserId, selectedIds, setSelectedIds }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [creatingBlank, setCreatingBlank] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("fileSortBy") || "created_desc");
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("fileSortBy", sortBy);
  }, [sortBy]);

  const filtered = useMemo(() => {
    const base = q.trim()
      ? files.filter((f) => f.name.toLowerCase().includes(q.trim().toLowerCase()))
      : files;
    const cmp = {
      created_desc: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      created_asc: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      updated_desc: (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      updated_asc: (a, b) => (a.updated_at || "").localeCompare(b.updated_at || ""),
      name_asc: (a, b) => (a.name || "").localeCompare(b.name || ""),
      name_desc: (a, b) => (b.name || "").localeCompare(a.name || ""),
    }[sortBy] || ((a, b) => 0);
    return [...base].sort(cmp);
  }, [q, files, sortBy]);

  const [currentFiles, archivedFiles] = useMemo(() => {
    const cur = [];
    const arc = [];
    for (const f of filtered) {
      if (f.archived_at) arc.push(f);
      else cur.push(f);
    }
    return [cur, arc];
  }, [filtered]);

  const [archiveOpen, setArchiveOpen] = useState(false);

  const doUploadOne = async (file, batchId) => {
    const low = file.name.toLowerCase();
    if (!ACCEPT_EXT.some((e) => low.endsWith(e))) {
      toast.error(t("error_upload_type"));
      return null;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(t("error_upload_size", { mb: MAX_MB }));
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    // One shared batch_id for all files in this upload session so the
    // backend can archive the previous batch atomically.
    if (batchId) fd.append("batch_id", batchId);
    const { data } = await api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const doUpload = async (fileList) => {
    const list = Array.from(fileList || []);
    if (list.length === 0) return;
    setUploading(true);
    let firstId = null;
    let ok = 0;
    let fail = 0;
    // One batch_id per upload session (all files share it).
    const batchId =
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

    // Parallel upload with a small concurrency limit — dramatically faster
    // than sequential uploads for multi-file selections, without hammering
    // the browser with dozens of simultaneous multipart requests.
    const CONCURRENCY = 4;
    let cursor = 0;
    const worker = async () => {
      while (cursor < list.length) {
        const idx = cursor++;
        const f = list[idx];
        try {
          const d = await doUploadOne(f, batchId);
          if (d) {
            ok += 1;
            if (idx === 0 && !firstId) firstId = d.id;
          } else {
            fail += 1;
          }
        } catch (err) {
          fail += 1;
          toast.error(`${f.name}: ${formatApiError(err)}`);
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, list.length) }, () => worker())
    );

    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (ok) toast.success(t("upload_success_n", { n: ok }));
    await onRefresh();
    if (firstId) onSelect(firstId);
  };

  const onFileChange = (e) => doUpload(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    doUpload(e.dataTransfer.files);
  };

  const createBlankFile = async () => {
    if (creatingBlank) return;
    setCreatingBlank(true);
    try {
      const { data } = await api.post("/files/new", { pages: 1 });
      toast.success("新建空白文档");
      await onRefresh?.();
      onSelect?.(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingBlank(false);
    }
  };

  const doRestore = async (f) => {
    try {
      await api.post(`/files/${f.id}/unarchive`);
      toast.success(`已还原 · ${f.name}`);
      await onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((f) => selectedIds.has(f.id));
  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const f of filtered) next.delete(f.id);
      } else {
        for (const f of filtered) next.add(f.id);
      }
      return next;
    });
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/files/bulk-delete", { file_ids: ids });
      toast.success(`${data.deleted} deleted`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      if (activeFileId && selectedIds.has(activeFileId)) onSelect(null);
      await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const bulkDownload = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const loadingToast = toast.loading(t("bulk_download"));
    try {
      const resp = await api.post(
        "/files/bulk-download",
        { file_ids: ids },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `annotated-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.dismiss(loadingToast);
      toast.success(t("export_success"));
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(formatApiError(err));
    }
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    const newName = renameValue.trim();
    if (!newName) return;
    try {
      await api.patch(`/files/${renameTarget.id}/rename`, { name: newName });
      toast.success(t("rename_success"));
      setRenameTarget(null);
      await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/files/${deleteTarget.id}`);
      toast.success(t("delete_success"));
      setDeleteTarget(null);
      await onRefresh();
      if (activeFileId === deleteTarget.id) onSelect(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <aside
      data-testid={WORKSPACE.sidebar}
      className="w-80 xl:w-96 flex-shrink-0 flex flex-col border-r border-[hsl(var(--brand)/0.15)] bg-[hsl(var(--brand-tint))]/40 relative"
    >
      {/* Header: upload + search */}
      <div className="p-4 space-y-3 border-b border-[hsl(var(--brand)/0.15)] bg-background">
        <div className="flex gap-2">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center tx-bg cursor-pointer ${dragging ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand-tint))]" : "border-[hsl(var(--brand)/0.35)] hover:border-[hsl(var(--brand))] hover:bg-[hsl(var(--brand-tint))]"}`}
            onClick={() => inputRef.current?.click()}
            data-testid={WORKSPACE.uploadBtn}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT_EXT.join(",")}
              className="hidden"
              onChange={onFileChange}
              data-testid={WORKSPACE.uploadInput}
            />
            <Upload className="w-5 h-5 mx-auto mb-2 text-[hsl(var(--brand))]" strokeWidth={1.75} />
            <div className="text-xs font-semibold uppercase tracking-widest">
              {uploading ? t("uploading") : t("upload")}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {t("drag_drop_hint", { mb: MAX_MB })}
            </div>
          </div>
          <button
            data-testid="new-blank-file-btn"
            title="新建空白 PDF"
            onClick={createBlankFile}
            disabled={creatingBlank}
            className="w-16 flex-shrink-0 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-[hsl(var(--brand)/0.35)] hover:border-[hsl(var(--brand))] hover:bg-[hsl(var(--brand-tint))] tx-bg disabled:opacity-50"
          >
            <FilePlus2 className="w-5 h-5 text-[hsl(var(--brand))]" strokeWidth={1.75} />
            <span className="text-[10px] font-semibold uppercase tracking-widest">
              {creatingBlank ? "…" : "新建"}
            </span>
          </button>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <input
            data-testid={WORKSPACE.searchInput}
            className="w-full h-10 pl-9 pr-3 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand)/0.3)] focus:border-[hsl(var(--brand))] tx-bg"
            placeholder={t("search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="sidebar-select-all"
              checked={allVisibleSelected}
              onChange={toggleAll}
              className="w-3.5 h-3.5 accent-primary cursor-pointer"
            />
            <span>{t("files")}</span>
          </label>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="sort-btn"
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-sm border border-border hover:bg-accent tx-bg text-[10px]"
                  title={t("sort_by")}
                >
                  <ArrowUpDown className="w-3 h-3" strokeWidth={1.75} />
                  <span>{t(`sort_${sortBy}`)}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                {[
                  "created_desc",
                  "created_asc",
                  "updated_desc",
                  "updated_asc",
                  "name_asc",
                  "name_desc",
                ].map((key) => (
                  <DropdownMenuItem
                    key={key}
                    data-testid={`sort-${key}`}
                    onClick={() => setSortBy(key)}
                    className="flex items-center justify-between gap-2 text-xs cursor-pointer"
                  >
                    <span>{t(`sort_${key}`)}</span>
                    {sortBy === key && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span>{t("files_count", { count: filtered.length })}</span>
          </div>
        </div>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex-1">
              {t("bulk_selected_n", { n: selectedIds.size })}
            </span>
            <button
              data-testid="bulk-download-btn"
              onClick={bulkDownload}
              className="btn-swiss h-8 px-2 text-xs gap-1"
              title={t("bulk_download")}
            >
              <Archive className="w-3.5 h-3.5" strokeWidth={1.75} />
              {t("bulk_download")}
            </button>
            <button
              data-testid="bulk-delete-btn"
              onClick={() => setBulkDeleteOpen(true)}
              className="btn-swiss danger h-8 px-2 text-xs gap-1"
              title={t("bulk_delete")}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
              {t("bulk_delete")}
            </button>
          </div>
        )}
      </div>

      {/* File lists (current batch + archived) */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">…</div>
        ) : filtered.length === 0 ? (
          <div data-testid={WORKSPACE.sidebarEmpty} className="p-6 text-center">
            <FileIcon className="w-8 h-8 mx-auto text-muted-foreground/60 mb-2" strokeWidth={1.25} />
            <div className="font-display font-semibold text-sm">{t("empty_no_files")}</div>
            <div className="text-xs text-muted-foreground mt-1">{t("empty_no_files_hint")}</div>
          </div>
        ) : (
          <>
            {currentFiles.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                active={f.id === activeFileId}
                selected={selectedIds.has(f.id)}
                onToggleSelect={() => toggleOne(f.id)}
                currentUserId={currentUserId}
                onSelect={() => onSelect(f.id)}
                onRename={() => { setRenameTarget(f); setRenameValue(f.name.replace(/\.(xlsx|xlsm|pdf|docx?|pptx?)$/i, "")); }}
                onDelete={() => setDeleteTarget(f)}
              />
            ))}
            {archivedFiles.length > 0 && (
              <div className="border-t border-border/60">
                <button
                  data-testid="archive-toggle"
                  onClick={() => setArchiveOpen((v) => !v)}
                  className="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-accent tx-bg text-left"
                >
                  {archiveOpen ? (
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.75} />
                  )}
                  <Archive className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                  <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                    {t("archived_files", { count: archivedFiles.length })}
                  </span>
                </button>
                {archiveOpen && (
                  <div className="opacity-80">
                    {archivedFiles.map((f) => (
                      <FileRow
                        key={f.id}
                        file={f}
                        active={f.id === activeFileId}
                        selected={selectedIds.has(f.id)}
                        onToggleSelect={() => toggleOne(f.id)}
                        currentUserId={currentUserId}
                        onSelect={() => onSelect(f.id)}
                        onRename={() => { setRenameTarget(f); setRenameValue(f.name.replace(/\.(xlsx|xlsm|pdf|docx?|pptx?)$/i, "")); }}
                        onDelete={() => setDeleteTarget(f)}
                        onRestore={() => doRestore(f)}
                        archived
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{t("rename")}</DialogTitle>
          </DialogHeader>
          <input
            data-testid={WORKSPACE.renameInput}
            className="w-full h-11 px-3 border border-border rounded-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary tx-bg"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={t("rename_placeholder")}
            autoFocus
          />
          <DialogFooter>
            <button className="btn-swiss" onClick={() => setRenameTarget(null)}>Cancel</button>
            <button
              data-testid={WORKSPACE.renameConfirm}
              className="btn-swiss primary"
              onClick={submitRename}
            >
              {t("rename")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">{t("delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_delete", { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={WORKSPACE.deleteConfirm}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">{t("bulk_delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("bulk_selected_n", { n: selectedIds.size })} — {t("confirm_delete", { name: `${selectedIds.size}` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bulk-delete-confirm"
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={bulkDelete}
            >
              {t("bulk_delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

function KindIcon({ kind, className }) {
  if (kind === "pdf") return <FileText className={className} strokeWidth={1.5} />;
  if (kind === "word") return <FileText className={className} strokeWidth={1.5} />;
  if (kind === "ppt") return <Presentation className={className} strokeWidth={1.5} />;
  if (kind === "excel") return <FileSpreadsheet className={className} strokeWidth={1.5} />;
  return <FileIcon className={className} strokeWidth={1.5} />;
}

function FileThumb({ file }) {
  // Try loading the backend-generated thumbnail. If the endpoint returns 204
  // (thumb not ready yet) we retry every 3s up to a few times. On any error
  // we simply fall back to the file-kind icon so the row never breaks.
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let attempts = 0;
    const url = `${API_BASE}/files/${file.id}/thumb.png`;

    const load = async () => {
      attempts += 1;
      try {
        const resp = await fetch(url, { credentials: "include" });
        if (cancelled) return;
        if (resp.status === 204) {
          // Not ready yet — retry up to 5 times (15 s total).
          if (attempts < 5) retryTimer = setTimeout(load, 3000);
          return;
        }
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        setSrc(URL.createObjectURL(blob));
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [file.id]);

  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src);
    };
  }, [src]);

  if (failed || !src) {
    return (
      <div className="w-10 h-14 flex-shrink-0 border border-border/60 bg-muted/40 flex items-center justify-center rounded-sm">
        <KindIcon kind={file.kind} className="w-4 h-4 text-muted-foreground/70" />
      </div>
    );
  }
  return (
    <img
      data-testid={`file-thumb-${file.id}`}
      src={src}
      alt=""
      loading="lazy"
      className="w-10 h-14 flex-shrink-0 object-cover object-top border border-border/60 rounded-sm bg-white"
    />
  );
}


function FileRow({ file, active, selected, onToggleSelect, onSelect, onRename, onDelete, onRestore, currentUserId, archived }) {
  const { t, i18n } = useTranslation();
  const lockedByOther = file.lock?.held && file.lock?.holder_id !== currentUserId;
  const lockedByMe = file.lock?.held && file.lock?.holder_id === currentUserId;

  const dateLabel = useMemo(() => {
    if (!file.created_at) return "";
    try {
      const d = new Date(file.created_at);
      const locale = i18n.language === "zh" ? "zh-CN" : "en-US";
      return d.toLocaleString(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }, [file.created_at, i18n.language]);

  // Days remaining before this archived file gets purged (server TTL = 3d)
  const purgeInDays = useMemo(() => {
    if (!file.archived_at) return null;
    try {
      const archivedMs = new Date(file.archived_at).getTime();
      const purgeMs = archivedMs + 3 * 24 * 60 * 60 * 1000;
      const remainMs = purgeMs - Date.now();
      return Math.max(0, Math.ceil(remainMs / (24 * 60 * 60 * 1000)));
    } catch {
      return null;
    }
  }, [file.archived_at]);

  return (
    <div
      data-testid={WORKSPACE.fileItem(file.id)}
      onClick={onSelect}
      className={`file-item cursor-pointer px-4 py-3 border-b border-border/40 flex items-start justify-between gap-2 ${active ? "active" : ""} ${selected ? "bg-primary/5" : ""}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        onClick={(e) => e.stopPropagation()}
        data-testid={`file-checkbox-${file.id}`}
        className="w-4 h-4 mt-1 accent-primary cursor-pointer flex-shrink-0"
      />
      <FileThumb file={file} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold truncate">{file.name}</div>
          {file.annotation_count > 0 && (
            <span
              data-testid={`file-annotated-${file.id}`}
              title={`${file.annotation_count} annotations`}
              className="ml-auto inline-flex items-center gap-1 px-1.5 h-4 rounded-sm border border-primary/60 bg-primary/10 text-primary text-[9px] font-mono font-bold tracking-wider flex-shrink-0"
            >
              <MessageSquareText className="w-2.5 h-2.5" strokeWidth={2.25} />
              {file.annotation_count}
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          <CalendarClock className="w-3 h-3" strokeWidth={1.75} />
          <span data-testid={`file-uploaded-${file.id}`}>{dateLabel}</span>
        </div>
        {archived && purgeInDays != null && (
          <div
            data-testid={`file-purge-in-${file.id}`}
            className="mt-1 inline-flex items-center gap-1 px-1.5 h-4 rounded-sm border border-destructive/40 text-destructive/90 text-[9px] font-mono font-bold uppercase tracking-wider"
          >
            <Clock className="w-2.5 h-2.5" strokeWidth={2.25} />
            {t("purge_in_days", { n: purgeInDays })}
          </div>
        )}
        <div className="mt-0.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground truncate">
          {file.owner_email}
        </div>
        {(lockedByOther || lockedByMe) && (
          <div className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider ${lockedByOther ? "text-destructive" : "text-primary"}`}>
            <Lock className="w-3 h-3" strokeWidth={2} />
            <span className="truncate">
              {lockedByOther
                ? t("locked_by", { name: file.lock.holder_name || file.lock.holder_email })
                : t("editing_by_me")}
            </span>
          </div>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={WORKSPACE.fileMenuBtn(file.id)}
            className="p-1 rounded-sm hover:bg-accent tx-bg"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-sm">
          {archived && onRestore && (
            <>
              <DropdownMenuItem
                data-testid={`file-restore-${file.id}`}
                onClick={(e) => { e.stopPropagation(); onRestore(); }}
              >
                <ArchiveRestore className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> {t("restore")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            data-testid={WORKSPACE.renameBtn(file.id)}
            onClick={(e) => { e.stopPropagation(); onRename(); }}
          >
            <Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> {t("rename")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={WORKSPACE.deleteBtn(file.id)}
            className="text-destructive focus:text-destructive"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> {t("delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
